import React, { useState, useEffect } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Upload, Download, FileJson, AlertCircle, CheckCircle2, Loader2, FileSpreadsheet, Wrench, Clapperboard, Copy, Trash2, KeyRound, Database, PlayCircle, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import ExcelImportDialog from "@/components/ExcelImportDialog";
import { TableSkeleton, CardSkeleton } from "@/components/SkeletonLoader";

export default function ImportExportPage() {
  const [mode, setMode] = useState("merge");
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [showXlsx, setShowXlsx] = useState(false);
  const [repairing, setRepairing] = useState(false);

  const repairIds = async () => {
    if (!window.confirm("Vuoi convertire tutti gli ID dei clienti in formato stringa? È sicuro e risolve i casi 'Cliente non trovato'.")) return;
    setRepairing(true);
    try {
      const r = await api.post("/data/repair-ids");
      const f = r.data.fixed || {};
      toast.success(`Riparati: ${f.clients || 0} clienti, ${f.reminders || 0} promemoria, ${f.monthly || 0} pratiche`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setRepairing(false); }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setSummary(null);
    setImporting(true);
    try {
      const text = await file.text();
      const json = JSON.parse(text);

      // Try to detect formats: legacy LocalStorage shape (DB) or our export shape
      const payload = {
        clients: Array.isArray(json.clients) ? json.clients : null,
        reminders: Array.isArray(json.reminders) ? json.reminders : null,
        monthly: typeof json.monthly === "object" && !Array.isArray(json.monthly) ? json.monthly : null,
        mode,
      };
      const r = await api.post("/data/import", payload);
      setSummary(r.data.summary);
      toast.success("Import completato");
    } catch (e2) {
      console.error(e2);
      setError(e2?.response?.data?.detail || e2.message || "File non valido");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const exportAll = async () => {
    setExporting(true);
    try {
      const r = await api.get("/data/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `best-agent-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export pronto");
    } finally { setExporting(false); }
  };

  return (
    <AppLayout title="Import & Export" subtitle="Migrazione dati"
      actions={
        <button onClick={repairIds} disabled={repairing} data-testid="btn-repair-ids"
          className="text-sm flex items-center gap-2 px-3 py-2 border border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-[3px]">
          {repairing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          Ripara ID clienti
        </button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white p-6 shadow-[0_1px_3px_rgba(10,25,47,0.05)]" style={{ border: "1px solid #E8ECF2", borderRadius: 4, boxShadow: "0 1px 2px rgba(10,25,47,0.04)" }} data-testid="import-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#F5EFE3] text-[#8a6e3d] rounded-[3px] flex items-center justify-center"><Upload className="w-5 h-5" /></div>
            <h3 className="font-serif text-xl text-slate-900">Importa dal vecchio CRM</h3>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            Esporta dal CRM precedente un file JSON contenente <code className="font-mono text-xs bg-slate-100 px-1 rounded">clients</code>, <code >reminders</code> e/o <code >monthly</code>. Questo modulo è compatibile con la struttura LocalStorage <code>crm_v5r11_safe</code>.
          </p>
          <div className="mt-5 space-y-3">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Modalità</label>
            <div className="flex gap-2">
              <button onClick={() => setMode("merge")} data-testid="mode-merge" className={`flex-1 px-3 py-2 text-sm border rounded-[3px] ${mode === "merge" ? "border-[#0A192F] bg-[#0A192F] text-white" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>Unisci (upsert)</button>
              <button onClick={() => setMode("replace")} data-testid="mode-replace" className={`flex-1 px-3 py-2 text-sm border rounded-[3px] ${mode === "replace" ? "border-rose-600 bg-rose-600 text-white" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>Sostituisci (reset)</button>
            </div>
            <label className="block mt-3 cursor-pointer">
              <input type="file" accept="application/json" onChange={onPickFile} className="hidden" data-testid="import-file" disabled={importing} />
              <div className="flex items-center justify-center gap-2 py-6 border-2 border-dashed border-slate-300 rounded-[3px] hover:border-[#B7955B] hover:bg-slate-50 transition text-slate-700 text-sm">
                {importing ? <Loader2 className="w-4 h-4 text-[#8a6e3d]" /> : <FileJson className="w-5 h-5 animate-spin" />}
                {importing ? "Caricamento..." : "Seleziona file JSON"}
              </div>
            </label>

            <div className="pt-3 mt-3 border-t border-[#F0F2F5]">
              <button
                onClick={() => setShowXlsx(true)}
                data-testid="btn-import-xlsx"
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 rounded-[3px] text-emerald-800 text-sm font-medium transition"
              >
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" /> Importa da Excel (.xlsx) con mapping campi
              </button>
              <div className="text-[11px] text-slate-500 mt-1.5 text-center">Wizard 4 step: file → mappa colonne → opzioni → import. Aggiorna i duplicati.</div>
            </div>
            {summary && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-[3px] text-sm text-emerald-800 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="tabular-nums">
                  Importati <strong>{summary.clients}</strong> clienti, <strong>{summary.reminders}</strong> promemoria, <strong>{summary.monthly}</strong> pratiche.
                  {summary.skipped > 0 && <> Saltati: <strong>{summary.skipped}</strong>.</>}
                </div>
              </div>
            )}
            {error && (
              <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-[3px] text-sm text-rose-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white p-6 shadow-[0_1px_3px_rgba(10,25,47,0.05)]" style={{ border: "1px solid #E8ECF2", borderRadius: 4, boxShadow: "0 1px 2px rgba(10,25,47,0.04)" }} data-testid="export-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#0A192F] text-[#B7955B] rounded-[3px] flex items-center justify-center"><Download className="w-5 h-5" /></div>
            <h3 className="font-serif text-xl text-slate-900">Esporta tutto</h3>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            Scarica un backup JSON completo del tuo CRM (clienti, promemoria, pratiche del mese). Compatibile con il formato di import.
          </p>
          <button onClick={exportAll} disabled={exporting} data-testid="btn-export" className="mt-5 w-full text-white py-2.5 rounded-[3px] flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Scarica backup JSON
          </button>

          <div className="mt-5 pt-5 border-t border-[#F0F2F5]">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Esporta Excel</div>
            <div className="grid grid-cols-2 gap-2">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/excel/clients`}
                target="_blank"
                rel="noreferrer"
                data-testid="btn-export-xlsx-clients"
                className="flex items-center justify-center gap-2 px-3 py-2 text-sm border border-slate-300 rounded-[3px] hover:bg-slate-50 text-slate-700"
              >
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Clienti .xlsx
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/excel/monthly`}
                target="_blank"
                rel="noreferrer"
                data-testid="btn-export-xlsx-monthly"
                className="flex items-center justify-center gap-2 px-3 py-2 text-sm border border-slate-300 rounded-[3px] hover:bg-slate-50 text-slate-700"
              >
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Pratiche .xlsx
              </a>
            </div>
            <div className="text-[11px] text-slate-500 mt-2">Le richieste Excel usano i cookie di sessione del browser.</div>
          </div>
        </div>
      </div>
      <NoRecallExportCard />
      <BackupCard />
      <DemoVideoCard />
      <ExcelImportDialog open={showXlsx} onClose={() => setShowXlsx(false)} onImported={() => toast.info("Vai su Clienti per vedere i nuovi nominativi")} />
    </AppLayout>
  );
}

// ===========================================================================
// EXPORT ANALISI RECALL — ZIP sola lettura per ripianificazione clienti senza data
// ===========================================================================
function NoRecallExportCard() {
  const { user } = useAuth();
  const [running, setRunning] = useState(false);
  if (user?.role !== "admin") return null;

  const download = async () => {
    setRunning(true);
    try {
      const r = await api.get("/exports/no-recall", { responseType: "blob", timeout: 300000 });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="(.+)"/);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : "export_analisi_recall.zip"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export ZIP scaricato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore durante l'export"); }
    finally { setRunning(false); }
  };

  return (
    <div className="mt-5 bg-white p-6" style={{ border: "1px solid #E8ECF2", borderRadius: 4, boxShadow: "0 1px 2px rgba(10,25,47,0.04)" }} data-testid="norecall-export-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#B7955B] text-white rounded-[3px] flex items-center justify-center"><FileJson className="w-5 h-5" /></div>
        <div className="min-w-0">
          <h3 className="font-serif text-xl text-slate-900">Export Analisi Recall</h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            ZIP in <b>sola lettura</b> (nessun dato viene modificato): clienti <b>senza data recall</b> con storici integrali
            (aggiornamenti, esiti Boost, note, colonne personalizzate), pratiche collegate di tutto lo storico mensile,
            carico recall già pianificato per data/agente e documentazione dei campi. JSON UTF-8.
          </p>
        </div>
        <button onClick={download} disabled={running} data-testid="norecall-export-btn"
          className="ml-auto shrink-0 px-4 py-2.5 text-sm text-white rounded-[3px] flex items-center gap-2 disabled:opacity-60" style={{ background: "var(--navy)" }}>
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {running ? "Preparazione…" : "Scarica ZIP"}
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// BACKUP AUTOMATICO — notturno alle 03:00 + esecuzione manuale + download
// ===========================================================================
function BackupCard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [restoreFor, setRestoreFor] = useState(null);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try { const r = await api.get("/backups"); setItems(r.data.items || []); } catch {}
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!isAdmin) return null;

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/backups/run");
      toast.success(`Backup completato (${Math.round((r.data.backup?.size || 0) / 1024)} KB)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore backup"); }
    finally { setRunning(false); }
  };

  const download = async (b) => {
    try {
      const r = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = b.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
  };

  const fmtD = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const fmtSize = (s) => (s > 1024 * 1024 ? `${(s / 1024 / 1024).toFixed(1)} MB` : `${Math.round(s / 1024)} KB`);

  return (
    <div className="mt-5 bg-white p-6" style={{ border: "1px solid #E8ECF2", borderRadius: 4, boxShadow: "0 1px 2px rgba(10,25,47,0.04)" }} data-testid="backup-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-emerald-700 text-white rounded-[3px] flex items-center justify-center"><Database className="w-5 h-5" /></div>
        <div>
          <h3 className="font-serif text-xl text-slate-900">Backup Automatico</h3>
          <p className="text-xs text-slate-500">Ogni notte alle 03:00 salvo l&rsquo;intero CRM (clienti, pratiche, agenda, documenti, team) su storage sicuro</p>
        </div>
        <button onClick={run} disabled={running} data-testid="backup-run-btn"
          className="ml-auto px-4 py-2.5 text-sm text-white rounded-[3px] flex items-center gap-2 disabled:opacity-60 bg-emerald-700 hover:bg-emerald-800">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
          Esegui backup ora
        </button>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400 py-3" data-testid="backup-empty">Nessun backup ancora — il primo verrà creato automaticamente, oppure eseguilo ora.</p>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-[3px]">
          {items.slice(0, 8).map((b) => (
            <div key={b.id} className="px-3.5 py-2.5 flex items-center gap-3 text-[12.5px]" data-testid={`backup-row-${b.id}`}>
              <span className={`w-2 h-2 rounded-full shrink-0 ${b.trigger === "nightly" ? "bg-emerald-500" : "bg-[#B7955B]"}`} />
              <span className="font-medium text-slate-800">{fmtD(b.created_at)}</span>
              <span className="text-[10.5px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                {b.trigger === "nightly" ? "notturno" : b.trigger === "manual" ? "manuale" : "avvio"}
              </span>
              <span className="text-slate-500">{fmtSize(b.size)}</span>
              <span className="text-slate-400 hidden sm:inline">{b.counts?.clients ?? 0} clienti · {b.counts?.monthly ?? 0} pratiche · {b.counts?.reminders ?? 0} promemoria</span>
              <button onClick={() => setRestoreFor(b)} data-testid={`backup-restore-${b.id}`}
                className="ml-auto text-[11px] px-2.5 py-1.5 border border-amber-300 text-amber-700 hover:bg-amber-50 rounded-[3px] inline-flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Ripristina
              </button>
              <button onClick={() => download(b)} data-testid={`backup-dl-${b.id}`}
                className="p-1.5 text-slate-500 hover:text-[#B7955B] hover:bg-slate-50 rounded-[3px]" title="Scarica">
                <Download className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      {restoreFor && <RestoreDialog backup={restoreFor} onClose={() => setRestoreFor(null)} onDone={load} />}
    </div>
  );
}

// ===========================================================================
// RIPRISTINO DA BACKUP — anteprima differenze + ripristino selettivo
// ===========================================================================
function RestoreDialog({ backup, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({});
  const [restoring, setRestoring] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/backups/${backup.id}/restore-preview`);
        setPreview(r.data);
        const init = {};
        (r.data.collections || []).forEach((c) => { init[c.name] = c.name !== "users"; });
        setSel(init);
      } catch (e) { toast.error(e?.response?.data?.detail || "Errore anteprima"); onClose(); }
      finally { setLoading(false); }
    })();
  }, [backup.id]);

  const doRestore = async () => {
    const names = Object.keys(sel).filter((k) => sel[k]);
    if (names.length === 0) { toast.error("Seleziona almeno una collezione"); return; }
    const typed = window.prompt(`Stai per SOSTITUIRE ${names.length} collezioni con i dati del backup del ${new Date(backup.created_at).toLocaleString("it-IT")}.\n\nPrima del ripristino verrà creato automaticamente un backup di sicurezza.\n\nPer confermare digita: RIPRISTINA`);
    if ((typed || "").trim().toUpperCase() !== "RIPRISTINA") return;
    setRestoring(true);
    try {
      const r = await api.post(`/backups/${backup.id}/restore`, { collections: names, confirm: "RIPRISTINA" });
      setResult(r.data);
      toast.success("Ripristino completato");
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore ripristino"); }
    finally { setRestoring(false); }
  };

  const fmtD = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="restore-dialog">
      <div className="bg-white rounded-[4px] shadow-2xl w-full max-w-3xl max-h-[88vh] overflow-auto">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2.5 sticky top-0 bg-white">
          <RotateCcw className="w-4 h-4 text-amber-600" />
          <h3 className="font-serif text-lg text-slate-900">Ripristina dal backup del {fmtD(backup.created_at)}</h3>
          <button onClick={onClose} className="ml-auto p-1.5 hover:bg-slate-50 rounded" data-testid="restore-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Confronto il backup con i dati attuali…</div>
          ) : result ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-[3px] p-4 text-[13px] text-emerald-900" data-testid="restore-result">
              <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="w-4 h-4" /> Ripristino completato</div>
              <ul className="mt-2 list-disc list-inside space-y-0.5">
                {Object.entries(result.restored || {}).map(([k, v]) => <li key={k}><b>{k}</b>: {v} record ripristinati</li>)}
              </ul>
              <p className="mt-2 text-[12px]">Backup di sicurezza pre-ripristino: <b>{result.safety_backup}</b> — se ci ripensi, puoi ripristinare quello.</p>
              <button onClick={onClose} className="mt-3 px-4 py-2 text-sm bg-emerald-700 text-white rounded-[3px]">Chiudi</button>
            </div>
          ) : preview && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-[3px] p-3.5 text-[12.5px] text-amber-900 leading-relaxed mb-4">
                <b>Attenzione:</b> il ripristino <b>sostituisce completamente</b> le collezioni selezionate con i dati del backup.
                I record <b>creati dopo il backup andranno persi</b> (colonna rossa). Prima del ripristino viene creato in automatico un <b>backup di sicurezza</b>, quindi l&rsquo;operazione è reversibile.
              </div>
              <table className="w-full text-[12px]">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-[#FAFBFC]">
                  <tr>
                    <th className="text-left px-2.5 py-2">Dati</th>
                    <th className="text-right px-2.5 py-2">Nel backup</th>
                    <th className="text-right px-2.5 py-2">Attuali</th>
                    <th className="text-right px-2.5 py-2 text-emerald-700">Torneranno</th>
                    <th className="text-right px-2.5 py-2 text-rose-600">Persi (nuovi)</th>
                    <th className="text-right px-2.5 py-2 text-amber-700">Modificati</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {preview.collections.map((c) => (
                    <tr key={c.name} className={sel[c.name] ? "" : "opacity-45"}>
                      <td className="px-2.5 py-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={!!sel[c.name]} onChange={(e) => setSel((s) => ({ ...s, [c.name]: e.target.checked }))}
                            data-testid={`restore-check-${c.name}`} />
                          <span className="font-medium text-slate-800">{c.label}</span>
                          {c.name === "users" && <span className="text-[10px] text-rose-500">(sconsigliato)</span>}
                        </label>
                      </td>
                      <td className="text-right px-2.5 py-2 tabular-nums">{c.backup_count}</td>
                      <td className="text-right px-2.5 py-2 tabular-nums">{c.current_count}</td>
                      <td className="text-right px-2.5 py-2 tabular-nums text-emerald-700 font-semibold">{c.will_restore_deleted || "—"}</td>
                      <td className="text-right px-2.5 py-2 tabular-nums text-rose-600 font-semibold">{c.will_lose_new || "—"}</td>
                      <td className="text-right px-2.5 py-2 tabular-nums text-amber-700">{c.changed || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.collections.find((c) => c.name === "clients")?.sample_restored?.length > 0 && (
                <p className="mt-3 text-[11.5px] text-slate-500">
                  Esempi di clienti che tornerebbero: {preview.collections.find((c) => c.name === "clients").sample_restored.join(", ")}
                </p>
              )}
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={onClose} className="px-4 py-2.5 text-sm border border-slate-300 text-slate-600 rounded-[3px]">Annulla</button>
                <button onClick={doRestore} disabled={restoring} data-testid="restore-apply-btn"
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-[3px] flex items-center gap-2 disabled:opacity-60">
                  {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                  Ripristina selezionate
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// DATI DEMO VIDEO — account demo isolato per registrare lo schermo
// ===========================================================================
function DemoVideoCard() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [creds, setCreds] = useState(null);
  const isAdmin = user?.role === "admin";

  const loadStatus = async () => {
    try { const r = await api.get("/demo/status"); setStatus(r.data); } catch {}
  };
  useEffect(() => { if (isAdmin) loadStatus(); }, [isAdmin]);

  if (!isAdmin) return null;

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/demo/seed");
      setCreds(r.data);
      toast.success(`Dati demo pronti: ${r.data.counts?.clients || 0} clienti, ${r.data.counts?.monthly || 0} pratiche, ${r.data.counts?.reminders || 0} appuntamenti`);
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore generazione dati demo"); }
    finally { setSeeding(false); }
  };

  const reset = async () => {
    if (!window.confirm("Rimuovere TUTTI i dati demo (clienti, agenda e pratiche dell'account demo)? I dati reali non vengono toccati.")) return;
    setResetting(true);
    try {
      await api.post("/demo/reset");
      toast.success("Dati demo rimossi");
      setCreds(null);
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setResetting(false); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copiato"); };

  return (
    <div className="mt-5 bg-white p-6" style={{ border: "1px solid #E8ECF2", borderRadius: 4, boxShadow: "0 1px 2px rgba(10,25,47,0.04)" }} data-testid="demo-video-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-[#0A192F] text-[#B7955B] rounded-[3px] flex items-center justify-center"><Clapperboard className="w-5 h-5" /></div>
        <div>
          <h3 className="font-serif text-xl text-slate-900">Dati Demo Video</h3>
          <p className="text-xs text-slate-500">Account demo separato con soli dati finti — perfetto per registrare lo schermo</p>
        </div>
        {status?.clients > 0 && (
          <span className="ml-auto text-[11px] px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800" data-testid="demo-status-badge">
            {status.clients} clienti demo attivi
          </span>
        )}
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">
        Genera un set curato di <b>22 clienti finti</b> con stati distribuiti, note realistiche, recall di oggi,
        <b> 9 pratiche</b> con importi e <b>5 appuntamenti</b> in agenda — tutto assegnato all&rsquo;account
        <code className="mx-1 font-mono text-xs bg-slate-100 px-1 rounded">demo@bestagent.it</code>.
        Accedi con quell&rsquo;account per registrare il video: vedrai <b>solo</b> i dati finti. I tuoi clienti reali non vengono toccati.
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        <button onClick={seed} disabled={seeding} data-testid="demo-seed-btn"
          className="px-4 py-2.5 text-sm text-white rounded-[3px] flex items-center gap-2 disabled:opacity-60" style={{ background: "var(--navy)" }}>
          {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clapperboard className="w-4 h-4" />}
          {status?.clients > 0 ? "Rigenera dati demo" : "Genera dati demo video"}
        </button>
        {status?.clients > 0 && (
          <button onClick={reset} disabled={resetting} data-testid="demo-reset-btn"
            className="px-4 py-2.5 text-sm border border-rose-300 text-rose-700 hover:bg-rose-50 rounded-[3px] flex items-center gap-2 disabled:opacity-60">
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Rimuovi dati demo
          </button>
        )}
      </div>
      {creds && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-[3px] text-sm" data-testid="demo-credentials">
          <div className="flex items-center gap-2 font-semibold text-amber-900 mb-2"><KeyRound className="w-4 h-4" /> Credenziali account demo</div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-slate-600 w-20">Email</span>
              <code className="font-mono text-xs bg-white border border-slate-200 px-2 py-1 rounded flex-1">{creds.email}</code>
              <button onClick={() => copy(creds.email)} className="text-slate-500 hover:text-slate-900" data-testid="demo-copy-email"><Copy className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-600 w-20">Password</span>
              <code className="font-mono text-xs bg-white border border-slate-200 px-2 py-1 rounded flex-1">{creds.password}</code>
              <button onClick={() => copy(creds.password)} className="text-slate-500 hover:text-slate-900" data-testid="demo-copy-password"><Copy className="w-4 h-4" /></button>
            </div>
          </div>
          <p className="text-[11.5px] text-amber-800 mt-2.5">Esci da questo account e accedi con le credenziali demo per iniziare la registrazione.</p>
        </div>
      )}
    </div>
  );
}
